import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Pencil, Send, Plus, ChevronDown } from "lucide-react";
import { api, formatApiDetail } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { BLUPreview } from "./BLUPreview";
import PacketActions from "./admin/PacketActions";
import { BLUNotePreview } from "./BLUNotePreview";
import { AppAvatar } from "./AppAvatar";
import bluThreadAvatarUrl from "../assets/blu-thread-avatar.png";
import {
  normalizeBluFinalizePayload,
  parseAllBluFinalizes,
  resolveBluDocPayload,
  stripBluFinalizeBlock,
} from "../blu/parseBluFinalize";
import {
  normalizeBluNoteFinalizePayload,
  parseBluNoteFinalize,
  stripBluNoteFinalizeBlock,
} from "../blu/parseBluNoteFinalize";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import {
  bluPackAddThread,
  bluPackSetActive,
  createBluPack,
  deriveBluThreadTitle,
  loadBluMessages,
  loadBluPack,
  saveBluPack,
} from "../blu/bluChatStorage";
import {
  bluPackRenameThread,
  bluPackSetThreadClient,
  bluPackToggleArchive,
  bluPackTogglePin,
  sortBluThreads,
} from "../blu/bluThreadActions";
import { BluMarkdown } from "../blu/BluMarkdown";
import { wantsBluScopingContext, guessClientIdFromMessage } from "../blu/bluNotionIntent";
import { getBluPending } from "../blu/bluChatPending";
import {
  isBluChatRunning,
  reconcileOrphanBluPending,
  registerBluChatListener,
  runBluChatTurn,
  subscribeBluChatUpdated,
} from "../blu/bluChatRunner";
import { parseBluScopeBriefMarkdown, parseBluScopeOptionsMarkdown } from "../blu/parseBluFinalize";
import {
  BluBriefActions,
  BluClientPicker,
  BluContextStrip,
  BluPreviewDock,
  BluQuickStarters,
  BluScopeCards,
  BluScopeOptionActions,
  BluThreadMenu,
  hasBluFinalizeFence,
  isBluScopeOptions,
  isBluStep1Brief,
} from "./blu/BluInteractive";

export const BLU_SYSTEM_PROMPT = `You are BLU — the internal scope and invoice agent for TAĪSTU, a fractional
COO and product development practice that builds custom digital tools,
operational infrastructure, and brand systems for creative founder-led
businesses.

You only ever speak to Taī, the founder of TAĪSTU. You are never client-facing.
You are sharp, efficient, and warm. You don't over-explain. You match Taī's
energy — direct, intentional, grounded.

---

YOUR JOB

1. Identify which client or prospect Taī is scoping for
2. Ground the brief in attached system context, in this priority when present:
   - **Portal Overview Notes** ("## Portal Overview Notes") — curated monthly summary for active clients
   - **Prospect context** ("## Prospect context") — prospect record notes + studio meeting notes from Meetings (primary for outreach / met / proposal when Overview is empty)
   - **Notion** ("## Pulled Notion notes") — extra granular detail
   - Taī's chat — directing context, constraints, and anything the notes don't cover
3. Brief Taī on your understanding before building anything — confirm scope
   items and investment before generating documents. Call out what you pulled from meeting notes vs what Taī added in chat.
4. Generate an engagement packet in TAĪSTU's exact Flower Children World template format:
   - **cash / invoice** clients → Scope of Support + Invoice
   - **barter** clients → Scope of Support only (no invoice page; any offering: brand, web, or operational ecosystem)
   Fill the template with this prospect/client's relevant details only; do not invent work that contradicts the notes
5. On Taī's approval, upload the final PDF to the client's Library →
   Contracts & Invoices in the portal (same path for prospects). For **barter**, save as a scope PDF only (no INV number, no revenue invoice).

Always confirm the invoice number before finalizing **cash** packets. Invoice numbers are
auto-incremented from MongoDB starting at INV-101. Never reuse a number.
For **barter**, never invent or confirm an INV number — there is no invoice.

BARTER (high priority): PORTAL CLIENTS entries include compensation_type (cash|barter), service_tier, and engagement_type. Barter can apply to **any** offering.
- When Taī asks you to create a scope for an **ongoing operational ecosystem** engagement: produce a **fresh** packet — cash → scope + invoice; barter → scope only (agreement already replaced invoicing). Same ask-driven rhythm for barter ops and all ongoing ops ecosystem clients — you build when she asks, not unprompted.
- Barter + brand or web → scope only (same rule: no invoice).
In Step 1 use **EXCHANGE** instead of **INVESTMENT**. In blu-finalize set \`"scope_only": true\`, investment_total/subtotal/total to 0, put exchange text in \`"exchange"\`, and omit inventing invoice_service_* amounts.

---

TAĪSTU VOICE — HOW TO WRITE SCOPES

The canonical format is the Flower Children World scope (May 2026). All scopes
must match this style exactly. Do not use older Say Sukii or Studio 7 formats.

SCOPE FORMAT — PDF intro + scope_items (finalize JSON)

The intro field is **only** this shape — **the same single sentence as Step 1 DIRECTION** (one sentence; identical template — must still begin with: This document outlines the scope of our):
'This document outlines the scope of our [partnership type] for [Client Name] — [after the em dash: what this document/engagement is for — warm, specific, like the Flower Children World canonical example below. Not founder bio, revenue goals, or "came to TAĪSTU".]'

Partnership type — pick exactly one from the engagement (use invoice offering + scope; if unclear default to operational when it is ongoing ops/systems):
- Operational ecosystem / fractional ops + infrastructure → **operational partnership**
- Brand design + development → **branding partnership**
- Web design + development → **web design partnership**

Never substitute vague phrases ("creative partnership") when one of the three above fits. After the em dash, purpose and organizational character (FCW-style) are welcome; never founder-by-name biography, revenue goals, or "came to TAĪSTU" deal narrative.

Canonical example (illustrative client only — match this **shape**; swap partnership type, client name, and em-dash clause):
"This document outlines the scope of our operational partnership for Flower Children World — a phased build designed to bring structure, visibility, and infrastructure to a high vibrational youth programming organization that has been showing up since 2019 and is ready to grow with intention."

Other shapes (abbreviated):
- Branding: "This document outlines the scope of our branding partnership for [Client] — …"
- Web: "This document outlines the scope of our web design partnership for [Client] — …"

STEP 0 — SCOPE OPTIONS (opt-in only). When Taī wants multiple scopes to choose from (or taps Scope options), **she provides the packages** — you do not invent tiers, prices, or offerings.

Taī's input for each option includes (at minimum):
- Offering / partnership type (branding / web design / operational)
- Investment total
- What it entails (deliverables / boundaries)

You may also use Prospect context / Overview / Notion + her chat for voice and specificity, but **investment, offering type, and package boundaries come from Taī** — never invent a price or swap offerings she did not give.

If she asks for options **without** listing those packages yet: ask **one** tight question for the 2–3 options (offering + $ + what each includes). Do **not** draft ## SCOPE OPTIONS until she provides them. Do **not** invent Essential/Core/Full on your own.

When she has provided the packages, reply in **one message**:

Got it. Here's my read before I build anything:

Then:

## SCOPE OPTIONS

Client: [Name]

Then one block per package she defined (usually 3), using her labels/prices when given, else Option A/B/C with her investment:

### Option A · [Her label or short name] — $[her investment]

One short line mirroring her intent for this package.

Numbered **01 —** items in Flower Children voice that faithfully expand **what she said it entails** (plus relevant detail from meeting notes). Do not add major deliverables she did not include for that option.

### Option B · …

### Option C · …

Close with: she can build **all** into shareable Library PDFs for the prospect, or pick one into a Step 1 brief.

Do **not** emit \`\`\`blu-finalize\`\`\` inside the ## SCOPE OPTIONS draft message. Do **not** output \`## SCOPE OF SUPPORT\` in the same message as \`## SCOPE OPTIONS\`.

When Taī asks to build all for sharing (typed or portal chip), e.g. "Build all options into shareable scope packets for [Client].":
- Do **not** re-post the options menu or full Step 1 markdown.
- At most one short sentence, then emit **one \`\`\`blu-finalize\`\`\` JSON block per option** (usually 3), in order A/B/C.
- Each JSON must include \`"option_label": "Option A · [her label]"\` (and B/C) so Library titles stay distinct.
- Fill each finalize from that option's investment, offering (invoice_service_title), and items — her packages + notes. Same client_id/name/date_range across blocks unless she specified otherwise.
- Portal saves each as its own Scope + Invoice PDF for email/download to the prospect.

When Taī picks a single option (typed or portal chip), e.g. "Build Option B into a full Step 1 brief for [Client].": immediately output a **normal single** STEP 1 \`## SCOPE OF SUPPORT\` for that package only. Then wait for STEP 2 approval.

If Taī asks to scope without asking for options, skip STEP 0 and use STEP 1 as usual.

STEP 1 — First reply when Taī asks to scope (single package, no options): **one message only.** **Mandatory opening line** (exactly, then blank line):

Got it. Here's my read before I build anything:

Then **this structure only** — no other opener before it (no "On it!", no "Here's everything:"). After the blank line, output this heading literally (the portal draws a **divider line** under **## SCOPE OF SUPPORT**):

## SCOPE OF SUPPORT

[Client Name] on its own line — plain text. **Never** write "(company)" or any parenthetical after the name.

[Date range] on the next line — plain text. **Never** write "(timeline)" or any parenthetical after the date.

**DIRECTION**

Exactly **one sentence** — same as finalize \`intro\`; not a company bio or tagline. Must **begin** with: This document outlines the scope of our … then partnership type, " for ", client, " — ", purpose clause. Same rules as SCOPE FORMAT below (hard ban on "[Client] — a …-based …" taglines, etc.).

**WHAT WE'RE SCOPING**

Use this heading — **not** "SCOPE". Numbered lines **01 —**, **02 —**, … then 2–3 sentences each (body may follow on the next lines).

**INVESTMENT** (cash only)

Lines Taī should confirm ($, offering, issued/due, bill to as needed). No INV-XXX in Step 1.

**EXCHANGE** (barter only — use instead of INVESTMENT)

What they provide in place of fees. No dollar total. No Issued/Due. For ops/monthly barter, this cycle’s scope is the work package.

Then \`---\` on its own line, then: "Does this track, or do you want to adjust anything before I build it out?"

scope_items — only include priorities that belong in this scope (typically 2–4, add more only if warranted). Omit placeholder rows — never send empty title/description pairs; the portal PDF renders non-empty rows only and renumbers 01, 02… in array order.

SCOPE ITEMS — style (each row you include)
- Numbers in JSON/chat: 01, 02, … (match how many priorities you finalize; not bullets or ☉)
- Header: title-case, concise — two to four words
  e.g. "Operational Infrastructure", "Website Design + Development on Framer",
  "Funding Infrastructure + Community Capture"
- Description: 2–3 sentences. Specific. Names the actual deliverables,
  tools, audiences, and outcomes.
- Written in future-leaning declarative tone:
  "The site will include..." / "This includes structuring..." /
  "The goal is to build..."
- NOT "We'll..." (that's the old format)
- Never vague. Never padded. Every sentence earns its place.

STEP 2 — Approvals (typed or portal chips). Treat as Step 1 brief approved when Taī says (or the portal sends):
- "Looks good — build it out."
- yes / looks good / perfect / go for it / ship it / approve
Then go to STEP 3. Do **not** ask again whether to proceed.

STEP 2b — Adjust chips (portal sends these after a Step 1 brief). Do **not** emit finalize yet:
- "Adjust the scope items — I'll note what to change next." → Ask **one** tight question about which items to add/remove/rewrite, OR if the prior message already named changes, rewrite Step 1 immediately (full Step 1 layout again).
- "Change the investment — suggest a revised total for this brief." → Propose **one** revised dollar total with a one-line rationale, then re-render the full Step 1 brief with the new **INVESTMENT** (same layout). Wait for approval before STEP 3. (Skip this path for barter — adjust **EXCHANGE** instead if needed.)

STEP 2c — Rebuild from edited cards. When Taī's message starts with "Rebuild the Step 1 brief for" and includes revised numbered items + an investment total, treat that list as the new source of truth. Immediately output a fresh Step 1 brief (mandatory opener + ## SCOPE OF SUPPORT layout) using those items/titles/descriptions/total. Do not ask permission first. Then wait for STEP 2 approval. For barter rebuilds, investment may be 0 — keep **EXCHANGE**.

STEP 3 — After Step 2 approval: **do not** re-post the full brief or invoice prose in chat. At most **one short sentence** then **ONLY** the \`\`\`blu-finalize\`\`\` JSON block (portal shows the PDF card + preview dock; Taī saves to Library). No "Looks good? I can drop…" before the JSON.
- Cash: e.g. "Preview below — tap Save to put it in [Client]'s Library under Contracts & Invoices."
- Barter: e.g. "Preview below — tap Save to put this scope in [Client]'s Library under Contracts."

CLOSING DISCLAIMER (always, word for word)
*What's needed may evolve as we go. Priorities are set with intention
and held with flexibility.

RHYTHM OF WORK (always, exactly these three — numbered, not bulleted)
1  A shared client portal to track phases, milestones, and progress in real time.
2  Consistent touchpoints to stay aligned on direction, priorities, and momentum.
3  Regular updates shared on progress, decisions, and what comes next.

INVESTMENT & TERMS (cash only)
- Single line: just the dollar total
- No breakdown on the scope page — breakdown lives on the invoice only

EXCHANGE (barter only — use instead of INVESTMENT)
- What they provide in place of fees
- No dollar total; no Issued/Due invoice framing
- For ops/monthly barter, note that this cycle’s scope is the work package; the agreement already replaced invoicing

SIGN-OFF (always, exact formatting)
TAĪSTU — Operational Ecosystem    Thank you.

---

TAĪSTU VOICE — HOW TO WRITE INVOICES
(Cash / invoice clients only. Skip this entire section for barter — do not draft invoice prose.)

HEADER
TAĪSTU    tai@taistu.com
          taistu.com

Invoice
[INV-XXX]
Issued: [Month Day, Year]
Due: [Month Day, Year — same as issued unless Taī specifies otherwise]
Bill to: [Client Name]

SERVICES TABLE
- One or two line items maximum
- Description: plain, clear — what the engagement covers and the date range
- Amount right-aligned
- Finalize field invoice_service_description: **one sentence**, lowercase start, exact shape: an operational build for [Client] – [very short build clause] infrastructure, web presence, and community funding systems for [paste date_range verbatim]. Swap the pillar block for branding/web when the title is not operational; always reuse date_range exactly.

TOTALS
Subtotal     $[amount]
[Credit line only if applicable — e.g. "Referral Credit"]    -$[amount]
Total        $[amount]

PAYMENT
Please send payment directly via Apple Pay.
Apple Pay  857.399.3897

NOTES
- Warm, brief — one or two lines max
- Only include if there's something genuine to say (referral credit note,
  gratitude, context)
- Never filler

SIGN-OFF
TAĪSTU — Operational Ecosystem    Thank you.

---

WHAT TAĪSTU DOES NOT SOUND LIKE

Never write:
- "I am pleased to present..."
- "This proposal outlines our commitment to..."
- "We are excited to partner with you..."
- "Deliverables include but are not limited to..."
- "As per our discussion..."
- That you don't have their founding story / full Notion arc and need Taī to "fill in," "add a line," or "refine a placeholder" — draft the brief yourself
- Any corporate, stiff, or salesy language
- Vague scope items with no specifics
- Padding — every word must be doing work

---

CONVERSATION BEHAVIOR

PORTAL INTERACTIVE UI (compact BLU bubble) — Taī often acts via chips, not free typing. Treat these portal-sent messages as first-class intents:

Quick starters (composer chips):
- "Let's scope [Client]. Pull portal notes and draft a Step 1 brief." → Identify client, use attached Overview/Notion if present, produce Step 1 immediately.
- "Let's scope a client — ask me which one if unclear." → Ask for the client in one short line (or wait — Taī may pick from the client picker next).
- "Pull Overview notes and Notion context for [Client]." / "Pull notes for the client we're discussing." → Proceed as a scoping pull; if Overview/Notion blocks are attached, write or refresh Step 1 from them. Do not claim you are still fetching when blocks are already present.
- "Draft the invoice package for [Client] after the brief looks right." → If Step 1 is not yet approved, say so in one line and point them to **Looks good**. If already approved / finalize is next, go to STEP 3.
- "Revise the investment for [Client] — I'll suggest a new total." / "Revise the investment on the current brief." → Same as STEP 2b investment adjust: propose a total, re-render full Step 1.

Client picker:
- Taī may bind a client in the portal UI; messages will often include the studio name. Always prefer an explicit client name in the latest user message. Fuzzy-match against the portal client list. Never invent a client.

After every Step 1 brief, the portal shows interactive controls Taī can use (you do not render these — the portal does):
- Chips: **Looks good** / **Adjust scope** / **Change investment**
- Editable scope cards + **Rebuild from this** (sends the STEP 2c rebuild message)
- A preview dock when \`\`\`blu-finalize\`\`\` is present (Open / Save to Library) — Taī saves; you do not pretend to upload yourself

When Taī initiates:
- Detect the client name (fuzzy match against portal client list)
- Detect whether to fetch portal Overview + Notion context or use pasted context
- When "## Portal Overview Notes" is present and non-empty, treat it as the primary source for scope structure (Taī's curated monthly summary). Weight it above raw Notion transcripts; use "## Pulled Notion notes" for extra granular detail when both are present.
- If fetching, use whatever material the portal attached; when Overview or Notion blocks are present and non-empty, do not say you lack context — read them and write the brief.

Never use long DIRECTION-as-bio paragraphs or client taglines: "[Client] is a … founded by … / came to TAĪSTU / $10K goal / At the time there was no… / This month shifts focus to…" — use the single-sentence document opener template instead (see SCOPE FORMAT). The line must literally start with "This document outlines the scope of our".

When writing the DIRECTION section (Step 1):
- Under **DIRECTION**, paste **only** the one-sentence opener beginning with those exact words, then partnership type, " for ", client, " — ", purpose clause.
- Same line as finalize "intro" until Taī edits.

Example of what NOT to write under DIRECTION:
"Studio 7 Miami is a Miami-based creative studio founded by Seven — built around photo booth experiences… Seven came to TAĪSTU with a clear goal…"

When Taī approves the brief (Step 2 / Looks good chip):
- Follow STEP 3: optional one short line, then **only** the \`\`\`blu-finalize\`\`\` JSON block. Do **not** repeat the Step 1 markdown document in chat.

When Taī requests refinements (including Adjust scope / Change investment / Rebuild from cards):
- Follow STEP 2b / STEP 2c
- Prefer a full updated Step 1 brief over a partial fragment so the portal chips and editable cards stay in sync
- End with the same approval invite line when waiting again

When Taī saves from the preview / preview dock (portal writes Library):
- Confirm briefly: "Done — saved to [Client]'s Library (INV-XXX)." (use the real invoice number from the portal after save; omit the number only if you do not know it yet.)

Thread UI (pin / rename / archive) is portal-local — ignore it; it does not change your job.

---

MEETING SUMMARY IN PORTAL — separate workflow

Trigger phrase: "create a meeting summary in portal for [client name]"

This is **not** a scope + invoice flow. Do not emit \`\`\`blu-finalize\`\`\` for meeting summaries.

STEP 1 — Search Notion
- When Taī uses the trigger (or asks for a meeting summary in the portal), the portal may attach "## Pulled Notion notes" in system context. Read that material first.
- Notion meeting notes for clients are titled: **[Client Name] x TAĪSTU** (exact pattern).
- Surface what you find and **confirm with Taī before proceeding**. For each candidate include:
  - Note title
  - Date (from the note or metadata when visible)
  - One-line preview of the content
- **Wait for Taī to confirm** this is the correct note before moving forward. Do not draft the summary until confirmed.

STEP 2 — Generate summary
Once Taī confirms the source note, produce a meeting summary matching Taī's **exact portal format** (see canonical example below).

**Portal note title** (separate field — not in body):
[Client Name] x TAĪSTU

**Body content** — always this structure, in order:

1. Line 1: plain text — \`Meeting Summary | [Date]\` (e.g. \`Meeting Summary | May 26, 2026\`). Not a markdown # heading.
2. Blank line, then \`---\` divider on its own line
3. One or more topic sections — each section is:
   - **Bold section heading** on its own line (no trailing punctuation)
   - Plain prose paragraph on the next line(s) — 2–4 sentences, no bullets
4. Final section: **Action Items** (bold heading) followed by a checklist (\`- [ ]\` per item)

Canonical example (Studio 7 — match this shape exactly):

Meeting Summary | May 26, 2026

---

**Booking System**
The booking system is nearly complete. The remaining blocker is Stripe verification — once activated, the team will test the system and migrate all existing bookings. A walkthrough is planned once verification clears.

**Studio Space**
The studio space is ready for the summer season. Equipment inventory is complete and the team is aligned on the new booking flow for studio rentals.

**Website**
The website refresh is in progress. Homepage and booking pages are live; remaining work is on the gallery and contact sections.

**Action Items**
- [ ] Complete Stripe verification
- [ ] Migrate existing bookings
- [ ] Test checkout end-to-end
- [ ] Share meeting notes via Notion

Guidelines:
- Title lives in the \`title\` field only — never repeat it inside \`content\`
- Body always opens with \`Meeting Summary | [Date]\`, then divider, then sections
- Section headings are **bold**, no trailing punctuation — topic names like "Booking System", "Studio Space", "Website"
- Body copy is plain prose — no bullet points within topic sections
- Action items are the only checklist — always the last section
- Tone: professional but direct — written to share with a client
- Do not editorialize or add context that was not in the original notes

Present the **full draft** to Taī and wait for explicit approval on language and alignment.
Accept approval only when Taī says something like "approved", "looks good", "ship it", or "go ahead".
Do **not** upload until approved.

STEP 3 — Upload to portal
After approval only:
- At most one short line (e.g. "Preview below — tap Save to add this to [Client]'s Notes.")
- Then **only** a \`\`\`blu-note-finalize\`\`\` JSON block — no other prose after it.

Portal note fields:
- title: "[Client Name] x TAĪSTU"
- status: draft (INTERNAL in the portal UI)
- content: approved summary markdown, exactly as formatted above — no post-approval edits

Example block:

\`\`\`blu-note-finalize
{
  "client_id": "[portal client id]",
  "client_name": "[Client Name]",
  "title": "Studio 7 x TAĪSTU",
  "content": "Meeting Summary | May 26, 2026\\n\\n---\\n\\n**Booking System**\\nThe booking system is nearly complete. The remaining blocker is Stripe verification — once activated, the team will test the system and migrate all existing bookings.\\n\\n**Studio Space**\\nThe studio space is ready for the summer season. Equipment inventory is complete and the team is aligned on the new booking flow.\\n\\n**Website**\\nThe website refresh is in progress. Homepage and booking pages are live; remaining work is on the gallery and contact sections.\\n\\n**Action Items**\\n- [ ] Complete Stripe verification\\n- [ ] Migrate existing bookings\\n- [ ] Test checkout end-to-end"
}
\`\`\`

When Taī taps **Save to Notes** on the preview, confirm briefly: "Done — saved to [Client]'s Notes."

---

PDF STYLING (pass to PDF generator)

Font:         Nohemi Black (headings) / Nohemi Light (body)
Background:   #FCFCFC
Text:         #222222
Accent:       #001EFF
Page size:    Letter or A4
Pages:        2 — Page 1: Scope of Support / Page 2: Invoice
Margins:      generous — let it breathe

---

PORTAL — SAVE TO LIBRARY (required)

- Portal Overview Notes may appear as a "## Portal Overview Notes" block (from the client's Notes tab, e.g. "JUN/JUL Overview x [Client]" or "JUN | JUL Overview: [Client]"). When present and non-empty, the portal **already** fetched the Overview Taī named (or the latest matching one) — weight it as the primary scope source; do **not** ask Taī to paste it.
- Notion context may appear as a "## Pulled Notion notes" block in the system context **before this reply**. When that block is present and non-empty, the portal **already** fetched those notes — do **not** say you are about to pull, are pulling, or will pull Notion in this message. Start the Step 1 message with the mandatory "Got it. Here's my read…" opener, then the **## SCOPE OF SUPPORT** layout (client name + date on their own lines, then **DIRECTION** / **WHAT WE'RE SCOPING** / **INVESTMENT** or **EXCHANGE**).
- If Taī asks for Notion but there is **no** usable Pulled Notion block, do not pretend a fetch is running. Answer from Portal Overview Notes (if attached), what Taī wrote in chat, or ask them to paste meeting notes — briefly, one turn.
- In chat, show invoice number as INV-XXX placeholder only. The portal assigns the next Mongo number (INV-101+) when saving; never claim a live number until after save.

When Taī has approved the brief and you have emitted \`\`\`blu-finalize\`\`\`, Taī taps **Save** on the preview to write the PDF to Library → Contracts & Invoices (cash) or Contracts / scope (barter). Until then, invoice number in chat stays INV-XXX for cash; the portal assigns INV-101+ on save. For barter there is no INV.

Example finalize block (shape only — use on Step 2 approval per STEP 3):

\`\`\`blu-finalize
{
  "client_id": "[portal client id]",
  "client_name": "[Client Name]",
  "date_range": "[e.g. May 15 – Jun 15, 2026]",
  "intro": "This document outlines the scope of our operational partnership for Flower Children World — a phased build designed to bring structure, visibility, and infrastructure to a high vibrational youth programming organization that has been showing up since 2019 and is ready to grow with intention.",
  "scope_items": [
    {"number": "01", "title": "...", "description": "..."},
    {"number": "02", "title": "...", "description": "..."}
  ],
  "scope_only": false,
  "exchange": "",
  "investment_total": 1000,
  "invoice_service_title": "OPERATIONAL ECOSYSTEM",
  "invoice_service_description": "an operational build for Flower Children World – a phased rollout across infrastructure, web presence, and community funding systems for May 15 – Jun 15, 2026.",
  "subtotal": 1200,
  "credits": 200,
  "credit_label": "Referral Credit",
  "total": 1000,
  "issued_date": "May 1, 2026",
  "due_date": "May 1, 2026",
  "notes": "Thankful for you <3\\nIncluded is a $200 credit for referring TAĪSTU.",
  "option_label": null
}
\`\`\`

ALREADY PAID (cash only): When Taī says the client already paid (or paid on a specific date), include in blu-finalize:
- \`"paid": true\`
- \`"paid_at": "Aug 11, 2026"\` (use the date she gave; same display style as issued_date). If she says paid but gives no date, ask once for the paid date before Step 3.
Do not invent a paid date. On Save, Revenue marks the invoice PAID, the PDF shows Paid, and the client review invoice page reflects that.

For barter finalize: set \`"scope_only": true\`, put exchange text in \`"exchange"\`, set investment_total/subtotal/credits/total to 0, and leave invoice_service_* / issued_date / due_date empty or omit them.

When saving multiple shareable options in one reply, emit one fence per option and set \`"option_label": "Option A · Essential"\` (etc.) on each.
`;

function stripBluActionBlocks(content) {
  return stripBluNoteFinalizeBlock(stripBluFinalizeBlock(content));
}
const MSG_RECV_BAR = "bg-white text-[var(--t-text)] border border-[var(--t-border)]/50 shadow-none";
const MSG_SENT_BAR = "bg-[rgba(0,30,255,0.10)] text-[var(--t-text)] border border-[rgba(0,30,255,0.12)] shadow-none";
const CHAT_BG = "bg-transparent";
const COMPOSER_GLASS = "t-glass";
const COMPOSER_SEND_ACTIVE_TINT = "!bg-[rgba(0,30,255,0.1)] !border-[rgba(0,30,255,0.28)]";
const BUBBLE_RADIUS = "rounded-[var(--t-event-pill-radius)]";
/** Same in full-page BLU and floating popover (timestamps stay smaller below). */
const BLU_CHAT_BODY = "text-[12px] leading-snug font-light";
const BLU_CHAT_STAMP = "text-[10px] leading-tight text-[var(--t-meta)] tabular-nums";

function defaultBluGreeting() {
  const hour = new Date().getHours();
  // Evening: 5pm–4:59am (local time)
  return (hour >= 17 || hour < 5) ? "Peace Taī." : "Hey Taī 🌞";
}

function nowIso() {
  return new Date().toISOString();
}

function formatMessageTimestamp(iso) {
  if (typeof iso !== "string" || !iso.trim()) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function initialGreetingRow() {
  return [{ role: "assistant", content: defaultBluGreeting(), sentAt: nowIso() }];
}

/** Water photo avatar — message threads only (FAB / nav stay unchanged). */
function BluThreadAvatar({ size = 36 }) {
  return (
    <span
      className="mt-0.5 inline-flex shrink-0 self-start overflow-hidden rounded-full border border-[var(--t-border)] bg-[var(--t-bg)]"
      style={{ width: size, height: size }}
      aria-hidden
    >
      <img src={bluThreadAvatarUrl} alt="" className="h-full w-full object-cover" />
    </span>
  );
}

export default function BLUAgent({
  embedded = false,
  layout = "default",
  bubble = "compact",
  launchSeed = null,
  onLaunchSeedConsumed,
} = {}) {
  const { user } = useAuth();
  const [bluPack, setBluPack] = useState(() => {
    const p = loadBluPack();
    if (p?.threads?.length) return p;
    const g = initialGreetingRow();
    return createBluPack(g) || { version: 2, activeThreadId: "x", threads: [{ id: "x", title: "New chat", messages: g, updatedAt: nowIso() }] };
  });
  const [topicOpen, setTopicOpen] = useState(false);
  const messages = useMemo(() => {
    const t = bluPack.threads.find((x) => x.id === bluPack.activeThreadId);
    const m = t?.messages;
    return Array.isArray(m) && m.length ? m : initialGreetingRow();
  }, [bluPack]);

  const setMessages = useCallback((updater) => {
    setBluPack((p) => {
      const idx = p.threads.findIndex((t) => t.id === p.activeThreadId);
      if (idx < 0) return p;
      const cur = p.threads[idx].messages || [];
      const next = typeof updater === "function" ? updater(cur) : updater;
      if (!Array.isArray(next)) return p;
      const now = new Date().toISOString();
      const curThread = p.threads[idx];
      const title = curThread.customTitle ? curThread.title : deriveBluThreadTitle(next);
      const threads = p.threads.map((t, i) => (i === idx ? { ...t, messages: next, updatedAt: now, title } : t));
      return { ...p, threads };
    });
  }, []);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [finalizeBusy, setFinalizeBusy] = useState(false);
  /** Saved packets keyed by finalize index ("0", "1", …). */
  const [packetByKey, setPacketByKey] = useState({});
  const [noteFinalizeBusy, setNoteFinalizeBusy] = useState(false);
  const [clients, setClients] = useState([]);
  const [loadPhase, setLoadPhase] = useState(null);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editDraft, setEditDraft] = useState("");
  const [showClientPicker, setShowClientPicker] = useState(false);
  const [showArchivedThreads, setShowArchivedThreads] = useState(false);
  const previewAnchorRef = useRef(null);
  const threadScrollRef = useRef(null);
  const threadEndRef = useRef(null);
  const inputRef = useRef(null);
  const editRef = useRef(null);
  const senderName = (user?.name || "Taī").trim() || "Taī";

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/admin/clients");
        if (!cancelled && Array.isArray(data)) {
          setClients(
            data
              .map((c) => ({
                id: c.id,
                name: c.name,
                engagement_stage: c.engagement_stage || "",
                compensation_type: c.compensation_type || "cash",
                service_tier: c.service_tier || "",
                engagement_type: c.engagement_type || "",
                rate: c.rate,
                payment_structure: c.payment_structure || "",
              }))
              .filter((c) => c.id && c.name),
          );
        }
      } catch {
        /* optional */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    saveBluPack(bluPack);
  }, [bluPack]);

  useEffect(() => {
    if (!launchSeed) return;
    const clientId = launchSeed.clientId || launchSeed.client_id || null;
    const clientName = (launchSeed.clientName || launchSeed.client_name || "").trim();
    const stage = launchSeed.engagement_stage || launchSeed.engagementStage || "";
    if (clientId && clientName) {
      const client =
        clients.find((c) => c.id === clientId) || {
          id: clientId,
          name: clientName,
          engagement_stage: stage,
        };
      setBluPack((p) => bluPackSetThreadClient(p, p.activeThreadId, client));
    }
    if (launchSeed.text) {
      setInput(String(launchSeed.text));
      requestAnimationFrame(() => inputRef.current?.focus?.());
    }
    onLaunchSeedConsumed?.();
  }, [launchSeed, clients, onLaunchSeedConsumed]);

  useEffect(() => {
    const syncFromStorage = () => {
      reconcileOrphanBluPending();
      const p = loadBluPack();
      if (p?.threads?.length) setBluPack(p);
      const pending = getBluPending();
      setLoading(isBluChatRunning());
      setLoadPhase(pending?.phase ?? null);
    };
    syncFromStorage();
    const unsubEvent = subscribeBluChatUpdated(syncFromStorage);
    const unsubListener = registerBluChatListener();
    return () => {
      unsubEvent();
      unsubListener();
    };
  }, []);

  const scrollThreadToBottom = useCallback((behavior = "auto") => {
    const el = threadScrollRef.current;
    const end = threadEndRef.current;
    const run = () => {
      if (end) {
        end.scrollIntoView({ block: "end", behavior });
      }
      if (el) {
        el.scrollTop = Math.max(0, el.scrollHeight - el.clientHeight);
      }
    };
    requestAnimationFrame(() => {
      run();
      requestAnimationFrame(run);
    });
  }, []);

  useEffect(() => {
    setPacketByKey({});
    scrollThreadToBottom("auto");
    const timers = [50, 150, 350, 600].map((ms) => window.setTimeout(() => scrollThreadToBottom("auto"), ms));
    return () => timers.forEach((id) => window.clearTimeout(id));
  }, [bluPack.activeThreadId, scrollThreadToBottom]);

  useEffect(() => {
    const inner = threadEndRef.current?.parentElement;
    if (!inner || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(() => scrollThreadToBottom("auto"));
    ro.observe(inner);
    return () => ro.disconnect();
  }, [messages.length, loading, scrollThreadToBottom]);

  const resizeComposerInput = useCallback(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
  }, []);

  useEffect(() => {
    resizeComposerInput();
  }, [input, resizeComposerInput]);

  useEffect(() => {
    scrollThreadToBottom(loading ? "smooth" : "auto");
  }, [messages, loading, loadPhase, scrollThreadToBottom]);

  const beginNewThread = useCallback(() => {
    setTopicOpen(false);
    const g = initialGreetingRow();
    setBluPack((p) => bluPackAddThread(p, g));
  }, []);

  const sortedThreads = useMemo(
    () => sortBluThreads(bluPack.threads, { includeArchived: showArchivedThreads }),
    [bluPack.threads, showArchivedThreads],
  );

  const activeThread = useMemo(
    () => bluPack.threads.find((x) => x.id === bluPack.activeThreadId) || null,
    [bluPack],
  );

  const activeThreadTitle = activeThread?.title || "New chat";

  const selectClient = useCallback((client) => {
    if (!client?.id) return;
    setBluPack((p) => bluPackSetThreadClient(p, p.activeThreadId, client));
    setShowClientPicker(false);
    setInput((prev) => {
      const name = client.name;
      if (!prev.trim()) return `Let's work on ${name}. `;
      if (prev.toLowerCase().includes(String(name).toLowerCase())) return prev;
      return `${prev.trim()} (${name})`;
    });
    requestAnimationFrame(() => inputRef.current?.focus());
  }, []);

  const renderThreadPopover = () => (
    <Popover open={topicOpen} onOpenChange={setTopicOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`relative w-full flex items-center justify-center min-h-[40px] max-w-full box-border rounded-[var(--t-event-pill-radius)] !shadow-none pl-4 pr-11 text-[12px] font-light font-[inherit] text-[var(--t-text)] outline-none transition-[border-color] duration-[160ms] ease-out focus-visible:outline-none focus-visible:ring-0 ${COMPOSER_GLASS}`}
          data-testid="blu-thread-selector"
        >
          <span className="truncate text-center">{activeThreadTitle}</span>
          <ChevronDown size={17} strokeWidth={1.6} className="absolute right-3 top-1/2 -translate-y-1/2 opacity-45 shrink-0 text-[var(--t-text)] pointer-events-none" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        side="bottom"
        sideOffset={4}
        className="z-[60] box-border min-w-0 w-[var(--radix-popper-anchor-width)] overflow-hidden rounded-[var(--t-event-pill-radius)] border border-[var(--t-border)] p-0 text-[var(--t-text)] t-glass shadow-none data-[state=open]:zoom-in-100 data-[state=closed]:zoom-out-100 data-[side=bottom]:slide-in-from-top-0"
      >
        <div className="flex items-center justify-between gap-3 border-b border-[var(--t-border)] p-3">
          <div className={`${BLU_CHAT_BODY} tracking-wide text-[var(--t-text)]`}>Threads</div>
          <button
            type="button"
            className="text-[10px] uppercase tracking-[0.14em] text-[var(--t-meta)] hover:text-[var(--t-accent)]"
            onMouseDown={(e) => {
              e.preventDefault();
              setShowArchivedThreads((v) => !v);
            }}
          >
            {showArchivedThreads ? "Hide archived" : "Archived"}
          </button>
        </div>
        <div className="max-h-[340px] overflow-auto p-2">
          <button
            type="button"
            className="min-h-[44px] w-full border border-transparent px-3 py-2 text-left hover:border-[var(--t-border)]"
            onMouseDown={(e) => {
              e.preventDefault();
              beginNewThread();
            }}
            data-testid="blu-new-thread"
          >
            <div className={`font-heading ${BLU_CHAT_BODY} inline-flex items-center gap-2 tracking-tight`}>
              <Plus size={14} strokeWidth={1.6} />
              New thread
            </div>
          </button>
          <div className="my-2 border-t border-[var(--t-border)]" />
          {sortedThreads.map((t) => {
            const selected = t.id === bluPack.activeThreadId;
            const last = (t.messages || [])[(t.messages || []).length - 1];
            const preview =
              last?.role === "user"
                ? { label: "You", text: last.content }
                : last?.role === "assistant"
                  ? { label: "BLU", text: stripBluFinalizeBlock(last.content || "").slice(0, 120) }
                  : null;
            return (
              <div
                key={t.id}
                className={`flex min-h-[44px] items-start gap-1 rounded-xl border border-transparent px-1 py-1 hover:border-[var(--t-border)] ${selected ? "bg-[rgba(0,30,255,0.08)]" : ""}`}
              >
                <button
                  type="button"
                  className="min-w-0 flex-1 px-2 py-1.5 text-left"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    setBluPack((p) => bluPackSetActive(p, t.id));
                    setTopicOpen(false);
                  }}
                  data-testid={`blu-thread-option-${t.id}`}
                >
                  <div className={`font-heading ${BLU_CHAT_BODY} mb-1 flex items-center gap-1.5 tracking-tight`}>
                    {t.pinned ? <span className="text-[var(--t-accent)]">Pinned</span> : null}
                    <span className="truncate">{t.title || "Chat"}</span>
                  </div>
                  {preview && (
                    <div className={`${BLU_CHAT_BODY} truncate text-[var(--t-meta)]`}>
                      {preview.label}: {preview.text}
                    </div>
                  )}
                </button>
                <BluThreadMenu
                  thread={t}
                  onRename={(title) => setBluPack((p) => bluPackRenameThread(p, t.id, title))}
                  onTogglePin={() => setBluPack((p) => bluPackTogglePin(p, t.id))}
                  onToggleArchive={() => setBluPack((p) => bluPackToggleArchive(p, t.id))}
                />
              </div>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );

  const resolveDocPayloads = useCallback(
    (content, messageIndex) => {
      const many = parseAllBluFinalizes(content, { clients });
      if (many.length) return many;
      const one = resolveBluDocPayload(content, { clients, messages, messageIndex });
      return one ? [one] : [];
    },
    [clients, messages],
  );

  const finalizePayloads = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      if (messages[i].role !== "assistant") continue;
      const parsed = resolveDocPayloads(messages[i].content, i);
      if (parsed.length) return parsed;
    }
    return [];
  }, [messages, resolveDocPayloads]);

  const finalizePayload = finalizePayloads[0] || null;
  const packetResult = packetByKey["0"] || null;

  const lastFinalizeMessageIndex = useMemo(() => {
    for (let idx = messages.length - 1; idx >= 0; idx -= 1) {
      if (messages[idx].role !== "assistant") continue;
      if (resolveDocPayloads(messages[idx].content, idx).length) return idx;
    }
    return -1;
  }, [messages, resolveDocPayloads]);

  const boundClient = useMemo(() => {
    if (activeThread?.clientId) {
      const hit = clients.find((c) => c.id === activeThread.clientId);
      if (hit) return hit;
      if (activeThread.clientName) return { id: activeThread.clientId, name: activeThread.clientName };
    }
    const fromMsg = [...messages].reverse().find((m) => m.role === "user");
    if (fromMsg) {
      const id = guessClientIdFromMessage(fromMsg.content, clients);
      if (id) {
        const hit = clients.find((c) => c.id === id);
        if (hit) return hit;
      }
    }
    if (finalizePayload?.client_id) {
      return {
        id: finalizePayload.client_id,
        name: finalizePayload.client_name || "Client",
      };
    }
    return null;
  }, [activeThread, clients, messages, finalizePayload]);

  const lastBriefIndex = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      if (messages[i].role !== "assistant") continue;
      if (hasBluFinalizeFence(messages[i].content)) return -1;
      if (isBluScopeOptions(messages[i].content)) return -1;
      if (isBluStep1Brief(messages[i].content)) {
        const brief = parseBluScopeBriefMarkdown(messages[i].content, { clients });
        if (brief) return i;
      }
    }
    return -1;
  }, [messages, clients]);

  const lastOptionsIndex = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      if (messages[i].role !== "assistant") continue;
      if (hasBluFinalizeFence(messages[i].content)) return -1;
      // A later Step 1 means an option was already chosen — hide options chips.
      if (isBluStep1Brief(messages[i].content)) {
        const brief = parseBluScopeBriefMarkdown(messages[i].content, { clients });
        if (brief) return -1;
      }
      if (isBluScopeOptions(messages[i].content)) {
        const parsed = parseBluScopeOptionsMarkdown(messages[i].content, { clients });
        if (parsed?.options?.length) return i;
      }
    }
    return -1;
  }, [messages, clients]);

  const noteFinalizePayload = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      if (messages[i].role !== "assistant") continue;
      const parsed = parseBluNoteFinalize(messages[i].content);
      if (parsed) return parsed;
    }
    return null;
  }, [messages]);

  const lastNoteFinalizeMessageIndex = useMemo(() => {
    for (let idx = messages.length - 1; idx >= 0; idx -= 1) {
      if (messages[idx].role !== "assistant") continue;
      if (parseBluNoteFinalize(messages[idx].content)) return idx;
    }
    return -1;
  }, [messages]);

  /** Only the most recent user message may be edited (no rewriting older turns). */
  const lastUserMessageIndex = useMemo(() => {
    for (let idx = messages.length - 1; idx >= 0; idx -= 1) {
      if (messages[idx].role === "user") return idx;
    }
    return -1;
  }, [messages]);

  useEffect(() => {
    if (editingIndex === null) return;
    if (editingIndex !== lastUserMessageIndex) {
      setEditingIndex(null);
      setEditDraft("");
    }
  }, [editingIndex, lastUserMessageIndex]);

  const postFinalizePayload = async (payload) => {
    const body = normalizeBluFinalizePayload(payload);
    if (!body?.client_id || !body?.client_name) return null;
    const { data } = await api.post("/admin/blu/finalize", body);
    const inv = data?.invoice_number != null ? String(data.invoice_number) : "";
    return {
      fileId: data?.library_file_id || null,
      invoiceNumber: inv,
      clientId: body.client_id || payload.client_id || null,
      clientName: payload.client_name,
      optionLabel: payload.option_label || null,
    };
  };

  const finalizeScopeAt = async (idx) => {
    const payload = finalizePayloads[idx];
    if (!payload || finalizeBusy) return null;
    const key = String(idx);
    if (packetByKey[key]?.fileId) return packetByKey[key];
    setFinalizeBusy(true);
    try {
      const result = await postFinalizePayload(payload);
      if (!result) return null;
      setPacketByKey((prev) => ({ ...prev, [key]: result }));
      const label = payload.option_label ? ` (${payload.option_label})` : "";
      toast.success(
        result.invoiceNumber
          ? `Packet ready for ${payload.client_name}${label} (${result.invoiceNumber}).`
          : `Packet ready for ${payload.client_name}${label}.`,
      );
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: result.invoiceNumber
            ? `Done — packet ready for ${payload.client_name}${label} (${result.invoiceNumber}). Email the PDF or download it to send via text.`
            : `Done — packet ready for ${payload.client_name}${label}. Email the PDF or download it to send via text.`,
          sentAt: nowIso(),
        },
      ]);
      return result;
    } catch (e) {
      toast.error(formatApiDetail(e.response?.data?.detail, "Could not create scope + invoice packet"));
      return null;
    } finally {
      setFinalizeBusy(false);
    }
  };

  const finalizeScope = async () => finalizeScopeAt(0);

  const finalizeAllScopes = async () => {
    if (!finalizePayloads.length || finalizeBusy) return;
    const clientName = finalizePayloads[0]?.client_name || "client";
    setFinalizeBusy(true);
    const saved = [];
    try {
      for (let i = 0; i < finalizePayloads.length; i += 1) {
        const key = String(i);
        if (packetByKey[key]?.fileId) {
          saved.push(packetByKey[key]);
          continue;
        }
        const payload = finalizePayloads[i];
        try {
          const result = await postFinalizePayload(payload);
          if (!result) break;
          setPacketByKey((prev) => ({ ...prev, [key]: result }));
          saved.push(result);
        } catch (e) {
          toast.error(formatApiDetail(e.response?.data?.detail, "Could not create scope + invoice packet"));
          break;
        }
      }
      if (saved.length === finalizePayloads.length) {
        toast.success(`${saved.length} packets ready for ${clientName}.`);
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content: `Done — ${saved.length} scope packets are in ${clientName}'s Library. Share for review (Accept / Request changes), or email/download each PDF.`,
            sentAt: nowIso(),
          },
        ]);
      } else if (saved.length) {
        toast.success(`Saved ${saved.length} of ${finalizePayloads.length} packets for ${clientName}.`);
      }
    } finally {
      setFinalizeBusy(false);
    }
  };

  const finalizePortalNote = async () => {
    if (!noteFinalizePayload || noteFinalizeBusy) return;
    const body = normalizeBluNoteFinalizePayload(noteFinalizePayload);
    if (!body?.client_id || !body?.client_name || !body?.content) return;
    setNoteFinalizeBusy(true);
    try {
      await api.post("/admin/blu/create-portal-note", body);
      toast.success(`Saved to ${noteFinalizePayload.client_name}'s Notes.`);
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: `Done — saved to ${noteFinalizePayload.client_name}'s Notes.`,
          sentAt: nowIso(),
        },
      ]);
    } catch (e) {
      toast.error(formatApiDetail(e.response?.data?.detail, "Could not save note"));
    } finally {
      setNoteFinalizeBusy(false);
    }
  };

  const runChatFromMessages = async (updated, triggerText) => {
    const raw = triggerText.trim();
    setLoading(true);
    const wantsContext = wantsBluScopingContext(raw);
    const clientGuess = guessClientIdFromMessage(raw, clients) || boundClient?.id;
    setLoadPhase(wantsContext && clientGuess ? "notion" : "thinking");

    try {
      await runBluChatTurn({
        text: raw,
        messages: updated,
        clients,
        systemPrompt: BLU_SYSTEM_PROMPT,
        skipUserAppend: true,
        preferredClientId: boundClient?.id || null,
        onPhase: (phase) => {
          if (phase === "thinking" && wantsContext) setLoadPhase("scope");
          else setLoadPhase(phase);
        },
      });
      const saved = loadBluMessages();
      if (saved) setMessages(saved);
      setLoadPhase("ready");
      window.setTimeout(() => {
        setLoadPhase(null);
        setLoading(false);
        scrollThreadToBottom("smooth");
      }, 650);
    } catch {
      const saved = loadBluMessages();
      if (saved) setMessages(saved);
      setLoadPhase(null);
      setLoading(false);
      scrollThreadToBottom("smooth");
    }
  };

  const sendMessage = async (text) => {
    if (!text.trim() || isBluChatRunning()) return;
    let body = text.trim();
    if (boundClient?.name && !body.toLowerCase().includes(String(boundClient.name).toLowerCase())) {
      if (/^(looks good|yes|perfect|go for it|ship it|approve)\b/i.test(body) === false) {
        /* keep as-is for short approvals */
      }
    }
    const userMsg = { role: "user", content: body, sentAt: nowIso() };
    const updated = [...messages, userMsg];
    setMessages(updated);
    setInput("");
    setEditingIndex(null);
    setEditDraft("");
    setShowClientPicker(false);
    await runChatFromMessages(updated, body);
  };

  const onQuickStarter = (text) => {
    if (text === "__picker__") {
      setShowClientPicker(true);
      return;
    }
    void sendMessage(text);
  };

  const startEditMessage = (index, content) => {
    if (loading || isBluChatRunning()) return;
    if (index !== lastUserMessageIndex || lastUserMessageIndex < 0) return;
    setEditingIndex(index);
    setEditDraft(content);
    requestAnimationFrame(() => editRef.current?.focus());
  };

  const cancelEditMessage = () => {
    setEditingIndex(null);
    setEditDraft("");
  };

  const saveEditMessage = async (index) => {
    const trimmed = editDraft.trim();
    if (!trimmed || isBluChatRunning()) return;
    if (index !== lastUserMessageIndex || lastUserMessageIndex < 0) return;
    const truncated = messages.slice(0, index);
    const userMsg = { role: "user", content: trimmed, sentAt: nowIso() };
    const updated = [...truncated, userMsg];
    setMessages(updated);
    setEditingIndex(null);
    setEditDraft("");
    await runChatFromMessages(updated, trimmed);
  };

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };


  const renderMessageRow = (m, i) => {
    const own = m.role === "user";
    const docPayloads = !own ? resolveDocPayloads(m.content, i) : [];
    const docPayload = docPayloads[0] || null;
    const notePayload = !own && !docPayload ? parseBluNoteFinalize(m.content) : null;
    const display = own ? m.content : stripBluActionBlocks(m.content);
    const isLatestDoc = !own && docPayload && i === lastFinalizeMessageIndex;
    const multiDocs = docPayloads.length > 1;
    const allLatestSaved =
      isLatestDoc &&
      docPayloads.every((_, idx) => Boolean(packetByKey[String(idx)]?.fileId));
    const previewTeaser =
      !own &&
      !docPayload &&
      /preview below|tap save|contracts & invoices|re-emit the block/i.test(String(m.content || ""));
    const isLatestNote = !own && notePayload && i === lastNoteFinalizeMessageIndex;
    const prev = messages[i - 1];
    const showBluName = !own && (i === 0 || messages[i - 1]?.role === "user");
    const marginTop = i === 0 ? "" : prev?.role === m.role ? "mt-2" : "mt-6";

    if (docPayload) {
  return (
        <div key={i} className={`min-w-0 ${marginTop}`}>
          <div className="flex justify-start">
            <div className="flex max-w-[min(96%,40rem)] min-w-0 gap-2 w-full">
              <BluThreadAvatar size={36} />
              <div className="min-w-0 flex-1 flex flex-col gap-2">
                {showBluName ? <div className={`pl-1 ${BLU_CHAT_BODY} text-[var(--t-meta)]`}>BLU</div> : null}
                {display ? (
                  <div className={`min-w-0 w-full ${BUBBLE_RADIUS} px-3.5 py-2.5 ${MSG_RECV_BAR}`}>
                    <div className={`min-w-0 ${BLU_CHAT_BODY} break-words [overflow-wrap:anywhere]`}>
                      <BluMarkdown>{display}</BluMarkdown>
                    </div>
                  </div>
                ) : null}
                <div ref={isLatestDoc ? previewAnchorRef : undefined} className="flex flex-col gap-2">
                  {docPayloads.map((p, idx) => {
                    const saved = isLatestDoc ? packetByKey[String(idx)] : null;
                    return (
                      <BLUPreview
                        key={`${i}-${idx}-${p.option_label || "scope"}`}
                        payload={p}
                        busy={finalizeBusy && isLatestDoc}
                        onFinalize={() => (isLatestDoc ? finalizeScopeAt(idx) : undefined)}
                        interactive={isLatestDoc}
                        scopeOnly={
                          Boolean(p?.scope_only) ||
                          clients.some(
                            (c) =>
                              c.id === p?.client_id &&
                              String(c.compensation_type || "").toLowerCase() === "barter",
                          )
                        }
                        packetFileId={saved?.fileId || null}
                        packetInvoiceNumber={saved?.invoiceNumber || null}
                        packetActions={
                          saved?.fileId ? (
                            <PacketActions
                              fileId={saved.fileId}
                              clientId={p?.client_id || saved?.clientId || null}
                              size="sm"
                              className="w-full [&_button]:flex-1"
                            />
                          ) : null
                        }
                      />
                    );
                  })}
                </div>
                {isLatestDoc && !allLatestSaved ? (
                  <div
                    className={`min-w-0 w-full ${BUBBLE_RADIUS} px-3.5 py-2.5 ${MSG_RECV_BAR}`}
                    data-testid="blu-doc-packet-prompt"
                  >
                    <p className={`${BLU_CHAT_BODY} break-words [overflow-wrap:anywhere]`}>
                      {multiDocs
                        ? `Looks good? Save all ${docPayloads.length} packets for ${docPayload.client_name}, then Share for review or email/download each PDF.`
                        : `Looks good? Create the packet for ${docPayload.client_name}, then Share for review or email/download the PDF.`}
                    </p>
                    {multiDocs ? (
                      <button
                        type="button"
                        disabled={finalizeBusy}
                        onClick={finalizeAllScopes}
                        className="neo-cta mt-2 !min-h-8 !px-3 text-[10px]"
                        data-testid="blu-save-all-packets"
                      >
                        {finalizeBusy ? "Saving…" : "Save all to Library"}
                      </button>
                    ) : null}
                  </div>
                ) : null}
                {m.sentAt ? (
                  <time className={`pl-0.5 ${BLU_CHAT_STAMP}`} dateTime={m.sentAt}>
                    {formatMessageTimestamp(m.sentAt)}
                  </time>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (notePayload) {
      return (
        <div key={i} className={`min-w-0 ${marginTop}`}>
          <div className="flex justify-start">
            <div className="flex max-w-[min(96%,40rem)] min-w-0 gap-2 w-full">
              <BluThreadAvatar size={36} />
              <div className="min-w-0 flex-1 flex flex-col gap-2">
                {showBluName ? <div className={`pl-1 ${BLU_CHAT_BODY} text-[var(--t-meta)]`}>BLU</div> : null}
                {display ? (
                  <div className={`min-w-0 w-full ${BUBBLE_RADIUS} px-3.5 py-2.5 ${MSG_RECV_BAR}`}>
                    <div className={`min-w-0 ${BLU_CHAT_BODY} break-words [overflow-wrap:anywhere]`}>
                      <BluMarkdown>{display}</BluMarkdown>
                    </div>
                  </div>
                ) : null}
                <BLUNotePreview
                  payload={notePayload}
                  busy={noteFinalizeBusy && isLatestNote}
                  onFinalize={finalizePortalNote}
                  interactive={isLatestNote}
                />
                {isLatestNote ? (
                  <div
                    className={`min-w-0 w-full ${BUBBLE_RADIUS} px-3.5 py-2.5 ${MSG_RECV_BAR}`}
                    data-testid="blu-note-approval-prompt"
                  >
                    <p className={`${BLU_CHAT_BODY} break-words [overflow-wrap:anywhere]`}>
                      {`Looks good? I can add this to ${notePayload.client_name}'s Notes as an internal draft — tap Save when you are ready.`}
                    </p>
                  </div>
                ) : null}
                {m.sentAt ? (
                  <time className={`pl-0.5 ${BLU_CHAT_STAMP}`} dateTime={m.sentAt}>
                    {formatMessageTimestamp(m.sentAt)}
                  </time>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      );
    }

            return (
      <div key={i} className={`min-w-0 ${marginTop}`}>
        {own ? (
          <div className="flex justify-end">
            <div className="flex max-w-[min(88%,21rem)] sm:max-w-[70%] min-w-0 gap-2">
              <div className="min-w-0 flex-1 flex flex-col items-end gap-1">
                {editingIndex === i ? (
                  <div className={`min-w-0 w-full ${BUBBLE_RADIUS} px-3 py-2.5 ${MSG_SENT_BAR}`}>
                    <textarea
                      ref={editRef}
                      value={editDraft}
                      onChange={(e) => setEditDraft(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Escape") {
                          e.preventDefault();
                          cancelEditMessage();
                        }
                        if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                          e.preventDefault();
                          saveEditMessage(i);
                        }
                      }}
                      rows={4}
                      className={`min-h-[80px] w-full resize-y bg-transparent ${BLU_CHAT_BODY} text-[var(--t-text)] outline-none`}
                    />
                    <div className="mt-2 flex justify-end gap-2">
                      <button type="button" className={`t-btn-ghost px-2 py-1 ${BLU_CHAT_BODY}`} onClick={cancelEditMessage}>
                        Cancel
                      </button>
                      <button
                        type="button"
                        className={`t-btn-ghost px-2 py-1 ${BLU_CHAT_BODY} text-[var(--t-accent)]`}
                        onClick={() => saveEditMessage(i)}
                        disabled={!editDraft.trim() || loading}
                      >
                        Save & resend
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="group relative min-w-0 w-full">
                    <div
                      className={`min-w-0 w-full ${BUBBLE_RADIUS} px-3.5 py-2.5 ${MSG_SENT_BAR}`}
                      data-testid={`blu-message-${i}`}
                    >
                      <p className={`${BLU_CHAT_BODY} break-words whitespace-pre-wrap [overflow-wrap:anywhere]`}>
                        {display}
                      </p>
                    </div>
                    {i === lastUserMessageIndex ? (
                      <button
                        type="button"
                        className="absolute -left-1 top-1 flex h-7 w-7 -translate-x-full items-center justify-center rounded-full border border-[var(--t-border)] bg-[var(--t-bg)] text-[var(--t-meta)] opacity-0 shadow-sm transition-opacity group-hover:opacity-100 hover:text-[var(--t-text)] focus-visible:opacity-100"
                        aria-label="Edit latest message"
                        onClick={() => startEditMessage(i, display)}
                        disabled={loading || isBluChatRunning()}
                      >
                        <Pencil size={13} strokeWidth={1.5} />
                      </button>
                    ) : null}
                  </div>
                )}
                {editingIndex !== i && m.sentAt ? (
                  <time className={BLU_CHAT_STAMP} dateTime={m.sentAt}>
                    {formatMessageTimestamp(m.sentAt)}
                  </time>
                ) : null}
              </div>
              <span className="mt-0.5 inline-flex shrink-0 self-start" aria-hidden>
                <AppAvatar name={senderName} src={user?.avatar_url || null} size={36} />
              </span>
            </div>
          </div>
        ) : (
          <div className="flex justify-start">
            <div className="flex max-w-[min(88%,23rem)] sm:max-w-[72%] min-w-0 gap-2">
              <BluThreadAvatar size={36} />
              <div className="min-w-0 flex-1 flex flex-col gap-1">
                {showBluName ? <div className={`pl-1 ${BLU_CHAT_BODY} text-[var(--t-meta)]`}>BLU</div> : null}
                <div className={`min-w-0 ${BUBBLE_RADIUS} px-3.5 py-2.5 ${MSG_RECV_BAR}`} data-testid={`blu-message-${i}`}>
                  <div className={`min-w-0 ${BLU_CHAT_BODY} break-words [overflow-wrap:anywhere]`}>
                    <BluMarkdown>{display}</BluMarkdown>
                  </div>
                </div>
                {i === lastOptionsIndex ? (
                  <BluScopeOptionActions
                    content={m.content}
                    clients={clients}
                    clientName={boundClient?.name}
                    disabled={loading || isBluChatRunning()}
                    onPick={(msg) => sendMessage(msg)}
                  />
                ) : null}
                {i === lastBriefIndex ? (
                  <>
                    <BluBriefActions
                      disabled={loading || isBluChatRunning()}
                      onApprove={() => sendMessage("Looks good — build it out.")}
                      onAdjustScope={() =>
                        sendMessage("Adjust the scope items — I'll note what to change next.")
                      }
                      onAdjustInvestment={() =>
                        sendMessage("Change the investment — suggest a revised total for this brief.")
                      }
                    />
                    <BluScopeCards
                      content={m.content}
                      clients={clients}
                      disabled={loading || isBluChatRunning()}
                      onRebuild={(msg) => sendMessage(msg)}
                    />
                  </>
                ) : null}
                {previewTeaser ? (
                  <div
                    className={`min-w-0 w-full ${BUBBLE_RADIUS} border border-[var(--t-border)] bg-[#FAFBFC] px-3.5 py-2.5`}
                    data-testid="blu-preview-missing-hint"
                  >
                    <p className={`${BLU_CHAT_BODY} break-words text-[var(--t-meta)] [overflow-wrap:anywhere]`}>
                      Preview could not be built — BLU did not include a valid finalize block and no scope brief was found in this thread. Ask BLU to send the{" "}
                      <code className="text-[11px]">blu-finalize</code> JSON again, or start a new scope message with the client name.
                    </p>
                  </div>
                ) : null}
                {m.sentAt ? (
                  <time className={`pl-0.5 ${BLU_CHAT_STAMP}`} dateTime={m.sentAt}>
                    {formatMessageTimestamp(m.sentAt)}
                  </time>
                ) : null}
              </div>
            </div>
          </div>
        )}
              </div>
            );
  };

  const renderLoadingRow = () => {
    const phaseMeta = {
      notion: {
        label: "Pulling client context",
        detail: "Loading Overview notes and meeting context.",
      },
      thinking: {
        label: "Working on your reply",
        detail: "Reviewing the thread in TAĪSTU voice.",
      },
      scope: {
        label: "Building scope items",
        detail: "Drafting direction, priorities, and investment.",
      },
      ready: {
        label: "Ready for you",
        detail: "Reply is in — review below.",
      },
    };
    const meta = phaseMeta[loadPhase] || phaseMeta.thinking;
    return (
      <div className="mt-6 min-w-0">
        <div className="flex justify-start">
          <div className="flex max-w-[min(88%,23rem)] min-w-0 gap-2 sm:max-w-[72%]">
            <BluThreadAvatar size={36} />
            <div className="flex min-w-0 flex-1 flex-col gap-1">
              <div className={`pl-1 ${BLU_CHAT_BODY} text-[var(--t-meta)]`}>BLU</div>
              <div className={`min-w-0 ${BUBBLE_RADIUS} px-3.5 py-2.5 ${MSG_RECV_BAR}`}>
                <div className="flex flex-col gap-1 py-0.5" role="status" aria-live="polite">
                  <div className="flex items-center gap-2.5">
                    <span className="h-2 w-2 shrink-0 animate-pulse rounded-full bg-[#001EFF]" aria-hidden />
                    <span className="blu-agent-status-label">{meta.label}</span>
                  </div>
                  <p className={`m-0 pl-[18px] ${BLU_CHAT_BODY} text-[var(--t-meta)]`}>{meta.detail}</p>
                  <div className="mt-1 flex gap-1 pl-[18px]">
                    {["notion", "scope", "thinking", "ready"].map((key) => (
                      <span
                        key={key}
                        className={`h-1 flex-1 rounded-full ${
                          loadPhase === key || (loadPhase === "thinking" && key === "thinking")
                            ? "bg-[var(--t-accent)]"
                            : "bg-black/10"
                        }`}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderComposer = ({ mode = "overlay" } = {}) => {
    const docked = mode === "dock";
    const fieldCls = docked
      ? "t-input min-h-[40px] max-h-[120px] w-full min-w-0 flex-1 resize-none !rounded-xl !bg-white !px-4 !py-2.5 text-[12px] font-light placeholder:text-[12px] placeholder:text-[var(--t-meta)]"
      : `min-w-0 flex-1 min-h-[40px] max-h-[120px] max-w-full w-full box-border rounded-[var(--t-event-pill-radius)] !shadow-none px-4 py-2.5 text-[12px] font-light font-[inherit] text-[var(--t-text)] placeholder:text-[12px] placeholder:text-[var(--t-meta)] placeholder:font-light outline-none resize-none transition-[border-color] duration-[160ms] ease-out focus-visible:outline-none focus-visible:ring-0 ${COMPOSER_GLASS}`;
    const sendCls = docked
      ? `inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${
          input.trim() && !loading ? "neo-cta !min-h-10 !px-0" : "t-btn-secondary !min-h-10 !rounded-xl"
        }`
      : input.trim() && !loading
        ? `inline-flex items-center justify-center !h-10 !w-10 !min-h-10 !min-w-10 shrink-0 !rounded-[var(--t-event-pill-radius)] !shadow-none box-border text-[var(--t-text)] font-[inherit] transition-[background-color,border-color] duration-150 focus-visible:!outline-none focus-visible:!ring-0 disabled:cursor-not-allowed ${COMPOSER_GLASS} ${COMPOSER_SEND_ACTIVE_TINT}`
        : `inline-flex items-center justify-center !h-10 !w-10 !min-h-10 !min-w-10 shrink-0 !rounded-[var(--t-event-pill-radius)] !shadow-none transition-[background-color,border-color] duration-150 focus-visible:!outline-none focus-visible:!ring-0 disabled:cursor-not-allowed ${COMPOSER_GLASS}`;

    const controls = (
      <div className="flex min-w-0 flex-row flex-nowrap items-end gap-2">
        <textarea
          ref={inputRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKey}
          placeholder="Write a reply…"
          rows={1}
          className={fieldCls}
          data-testid="blu-input"
        />
        <button
          type="button"
          onClick={() => sendMessage(input)}
          disabled={!input.trim() || loading}
          className={sendCls}
          aria-label="Send"
          data-testid="blu-send"
        >
          <Send
            size={17}
            strokeWidth={1.5}
            aria-hidden
            className={`transition-colors duration-150 ${
              input.trim() && !loading
                ? docked
                  ? "text-white"
                  : "text-[var(--t-text)]"
                : "text-[var(--t-meta)]"
            }`}
          />
        </button>
      </div>
    );

    const interactive = (
      <>
        <BluQuickStarters
          clients={clients}
          selectedClient={boundClient}
          disabled={loading || isBluChatRunning()}
          onPick={onQuickStarter}
        />
        {showClientPicker ? (
          <BluClientPicker
            clients={clients}
            selectedId={boundClient?.id}
            onSelect={selectClient}
            onClose={() => setShowClientPicker(false)}
          />
        ) : null}
        {controls}
      </>
    );

    if (docked) {
      return (
        <div
          className="shrink-0 border-t border-[var(--t-border)]/40 bg-white/70 px-3 py-3 pb-[max(0.75rem,calc(0.75rem+env(safe-area-inset-bottom)))]"
          data-testid="blu-composer"
        >
          {interactive}
        </div>
      );
    }

    return (
      <div
        className="pointer-events-none absolute inset-x-0 bottom-0 z-20 bg-[rgba(252,252,252,0.28)] px-3 py-3 pb-[max(0.75rem,calc(0.75rem+env(safe-area-inset-bottom)))] backdrop-blur-[14px] [-webkit-backdrop-filter:blur(14px)] sm:px-6 md:px-3"
        data-testid="blu-composer"
      >
        <div className="pointer-events-auto">{interactive}</div>
      </div>
    );
  };

  const renderThreadRail = () => (
    <aside className="neo-panel flex min-h-0 flex-col overflow-hidden p-2" data-testid="blu-thread-rail">
      <div className="label-eyebrow px-3 pb-2 pt-2">Threads</div>
      <button
        type="button"
        onClick={beginNewThread}
        className="mb-1 flex w-full items-center gap-2 rounded-xl px-3 py-2.5 text-left text-[11px] uppercase tracking-[0.14em] text-[var(--t-accent)] hover:bg-[rgba(0,30,255,0.06)]"
        data-testid="blu-new-thread"
      >
        <Plus size={14} strokeWidth={1.6} /> New
      </button>
      <div className="t-scroll-soft min-h-0 flex-1 space-y-1 overflow-y-auto">
        {sortedThreads.map((t) => {
          const on = t.id === bluPack.activeThreadId;
          const last = (t.messages || [])[(t.messages || []).length - 1];
          const preview =
            last?.role === "user"
              ? last.content
              : last?.role === "assistant"
                ? stripBluFinalizeBlock(last.content || "").slice(0, 72)
                : "";
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setBluPack((p) => bluPackSetActive(p, t.id))}
              className={`flex w-full flex-col gap-0.5 rounded-xl px-3 py-2.5 text-left ${
                on
                  ? "bg-[rgba(0,30,255,0.08)] text-[var(--t-accent)]"
                  : "text-[var(--t-meta)] hover:text-[var(--t-text)]"
              }`}
              data-testid={`blu-thread-option-${t.id}`}
            >
              <span className="truncate font-heading text-sm !font-light text-[var(--t-text)]">
                {t.title || "Chat"}
              </span>
              {preview ? (
                <span className="truncate text-[11px] text-[var(--t-meta)]">{preview}</span>
              ) : null}
            </button>
          );
        })}
      </div>
    </aside>
  );

  const bluStyles = (
    <style>{`
      .blu-md p { margin: 0 0 0.65em; }
      .blu-md p:last-child { margin-bottom: 0; }
      @keyframes blu-agent-status-shine {
        0% { background-position: 100% 50%; }
        100% { background-position: -100% 50%; }
      }
      .blu-agent-status-label {
        display: inline-block;
        font-size: 12px;
        font-weight: 300;
        line-height: 1.375;
        letter-spacing: 0.01em;
        background: linear-gradient(
          90deg,
          var(--t-meta) 0%,
          var(--t-meta) 36%,
          var(--t-accent) 50%,
          var(--t-meta) 64%,
          var(--t-meta) 100%
        );
        background-size: 175% 100%;
        -webkit-background-clip: text;
        background-clip: text;
        color: transparent;
        animation: blu-agent-status-shine 2.1s ease-in-out infinite;
      }
      @media (prefers-reduced-motion: reduce) {
        .blu-agent-status-label {
          animation: none;
          color: var(--t-meta);
          background: none;
          -webkit-background-clip: unset;
          background-clip: unset;
        }
      }
    `}</style>
  );

  const threadScroll = (padClass) => (
    <div
      ref={threadScrollRef}
      className={`min-h-0 flex-1 overflow-y-auto overscroll-y-contain ${padClass}`}
      data-testid="blu-thread"
    >
      <div className="flex min-h-full flex-col justify-end gap-0">
        {messages.map((m, i) => renderMessageRow(m, i))}
        {loading ? renderLoadingRow() : null}
        <div ref={threadEndRef} aria-hidden className="h-px w-full shrink-0" />
      </div>
    </div>
  );

  const threadBodyDefault = (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <div className="relative min-h-0 w-full min-w-0 flex-1 overflow-hidden">
        <div
          ref={threadScrollRef}
          className={`absolute inset-0 z-0 overflow-y-auto overscroll-y-contain px-3 pt-3 pb-[max(5.75rem,calc(4.75rem+env(safe-area-inset-bottom)))] sm:px-6 md:px-3 ${CHAT_BG}`}
          data-testid="blu-thread"
        >
          <div className="flex min-h-full flex-col justify-end gap-0">
            {messages.map((m, i) => renderMessageRow(m, i))}
            {loading ? renderLoadingRow() : null}
            <div ref={threadEndRef} aria-hidden className="h-px w-full shrink-0" />
          </div>
        </div>
        {renderComposer({ mode: "overlay" })}
      </div>
      {bluStyles}
    </div>
  );

  if (embedded) {
    /* Bubble A — compact: thread picker + docked chat */
    if (bubble === "compact") {
      return (
        <div className="flex min-h-0 flex-1 flex-col bg-transparent" data-testid="blu-page-embedded" data-blu-bubble="compact">
          <header className="flex shrink-0 items-center gap-2 border-b border-[var(--t-border)]/40 px-3 py-2.5">
            <div className="min-w-0 flex-1">{renderThreadPopover()}</div>
            {activeThread ? (
              <BluThreadMenu
                thread={activeThread}
                onRename={(title) => setBluPack((p) => bluPackRenameThread(p, activeThread.id, title))}
                onTogglePin={() => setBluPack((p) => bluPackTogglePin(p, activeThread.id))}
                onToggleArchive={() => setBluPack((p) => bluPackToggleArchive(p, activeThread.id))}
              />
            ) : null}
            <button
              type="button"
              onClick={beginNewThread}
              className="neo-cta !min-h-9 !px-3"
              data-testid="blu-new-thread"
              aria-label="New thread"
            >
              <Plus size={14} strokeWidth={1.4} />
            </button>
          </header>
          <BluContextStrip
            clientName={boundClient?.name}
            loadPhase={loadPhase}
            threadTitle={activeThreadTitle}
            hasPreview={Boolean(finalizePayload)}
          />
          {threadScroll("px-3 pt-3 pb-3")}
          <BluPreviewDock
            payload={finalizePayload}
            payloads={finalizePayloads}
            packetFileId={packetResult?.fileId}
            packetInvoiceNumber={packetResult?.invoiceNumber}
            packetByKey={packetByKey}
            busy={finalizeBusy}
            onFinalize={finalizeScope}
            onFinalizeAt={finalizeScopeAt}
            onFinalizeAll={finalizeAllScopes}
            onScrollToPreview={() => previewAnchorRef.current?.scrollIntoView({ behavior: "smooth", block: "center" })}
          />
          {renderComposer({ mode: "dock" })}
          {bluStyles}
        </div>
      );
    }

    /* Bubble B — split: sessions list + active chat (mini merged desk) */
    if (bubble === "split") {
      return (
        <div
          className="flex min-h-0 flex-1 flex-col overflow-hidden bg-transparent sm:flex-row"
          data-testid="blu-page-embedded"
          data-blu-bubble="split"
        >
          <aside className="flex max-h-[38%] min-h-0 w-full shrink-0 flex-col border-b border-[var(--t-border)]/40 sm:max-h-none sm:w-[42%] sm:border-b-0 sm:border-r">
            <div className="flex shrink-0 items-center justify-between gap-2 px-3 py-2.5">
              <div>
                <div className="label-eyebrow mb-0.5">Sessions</div>
                <div className="font-heading text-lg !font-extralight tabular-nums text-[var(--t-accent)]">
                  {sortedThreads.length}
                </div>
              </div>
              <button
                type="button"
                onClick={beginNewThread}
                className="neo-cta !min-h-9 !px-3"
                data-testid="blu-new-thread"
              >
                <Plus size={14} strokeWidth={1.4} />
              </button>
            </div>
            <div className="t-scroll-soft min-h-0 flex-1 space-y-1 overflow-y-auto px-2 pb-2">
              {sortedThreads.map((t) => {
                const on = t.id === bluPack.activeThreadId;
                const last = (t.messages || [])[(t.messages || []).length - 1];
                const preview =
                  last?.role === "user"
                    ? last.content
                    : last?.role === "assistant"
                      ? stripBluFinalizeBlock(last.content || "").slice(0, 56)
                      : "";
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setBluPack((p) => bluPackSetActive(p, t.id))}
                    className={`flex w-full flex-col gap-0.5 rounded-xl px-2.5 py-2 text-left ${
                      on
                        ? "bg-[rgba(0,30,255,0.08)]"
                        : "hover:bg-black/[0.03]"
                    }`}
                    data-testid={`blu-thread-option-${t.id}`}
                  >
                    <span className="truncate font-heading text-[13px] !font-light text-[var(--t-text)]">
                      {t.title || "Chat"}
                    </span>
                    {preview ? (
                      <span className="truncate text-[10px] text-[var(--t-meta)]">{preview}</span>
                    ) : null}
                  </button>
                );
              })}
            </div>
          </aside>
          <div className="flex min-h-0 min-w-0 flex-1 flex-col">
            <div className="shrink-0 border-b border-[var(--t-border)]/40 px-3 py-2.5">
              <div className="label-eyebrow mb-0.5">Active</div>
              <div className="truncate font-heading text-base !font-light">{activeThreadTitle}</div>
            </div>
            <BluContextStrip
              clientName={boundClient?.name}
              loadPhase={loadPhase}
              threadTitle={null}
              hasPreview={Boolean(finalizePayload)}
            />
            {threadScroll("px-3 pt-3 pb-3")}
            <BluPreviewDock
              payload={finalizePayload}
              payloads={finalizePayloads}
              packetFileId={packetResult?.fileId}
              packetInvoiceNumber={packetResult?.invoiceNumber}
              packetByKey={packetByKey}
              busy={finalizeBusy}
              onFinalize={finalizeScope}
              onFinalizeAt={finalizeScopeAt}
              onFinalizeAll={finalizeAllScopes}
              onScrollToPreview={() => previewAnchorRef.current?.scrollIntoView({ behavior: "smooth", block: "center" })}
            />
            {renderComposer({ mode: "dock" })}
          </div>
          {bluStyles}
        </div>
      );
    }

    /* Bubble C — stack: horizontal past-thread chips, then chat */
    return (
      <div className="flex min-h-0 flex-1 flex-col bg-transparent" data-testid="blu-page-embedded" data-blu-bubble="stack">
        <header className="shrink-0 border-b border-[var(--t-border)]/40 px-3 py-2.5">
          <div className="mb-2 flex items-center justify-between gap-2">
            <div className="label-eyebrow mb-0">Threads</div>
            <button
              type="button"
              onClick={beginNewThread}
              className="text-[11px] uppercase tracking-[0.14em] text-[var(--t-accent)]"
              data-testid="blu-new-thread"
            >
              + New
            </button>
          </div>
          <div className="flex gap-1.5 overflow-x-auto pb-0.5 [-webkit-overflow-scrolling:touch]">
            {sortedThreads.map((t) => {
              const on = t.id === bluPack.activeThreadId;
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setBluPack((p) => bluPackSetActive(p, t.id))}
                  className={`shrink-0 rounded-full px-3 py-1.5 text-[11px] tracking-wide ${
                    on
                      ? "bg-[var(--t-text)] text-white"
                      : "bg-black/[0.04] text-[var(--t-meta)] hover:text-[var(--t-text)]"
                  }`}
                  data-testid={`blu-thread-option-${t.id}`}
                >
                  <span className="max-w-[9rem] truncate">
                    {t.pinned ? "• " : ""}
                    {t.title || "Chat"}
                  </span>
                </button>
              );
            })}
          </div>
        </header>
        <BluContextStrip
          clientName={boundClient?.name}
          loadPhase={loadPhase}
          threadTitle={activeThreadTitle}
          hasPreview={Boolean(finalizePayload)}
        />
        {threadScroll("px-3 pt-3 pb-3")}
        <BluPreviewDock
          payload={finalizePayload}
          payloads={finalizePayloads}
          packetFileId={packetResult?.fileId}
          packetInvoiceNumber={packetResult?.invoiceNumber}
          packetByKey={packetByKey}
          busy={finalizeBusy}
          onFinalize={finalizeScope}
          onFinalizeAt={finalizeScopeAt}
          onFinalizeAll={finalizeAllScopes}
          onScrollToPreview={() => previewAnchorRef.current?.scrollIntoView({ behavior: "smooth", block: "center" })}
        />
        {renderComposer({ mode: "dock" })}
        {bluStyles}
      </div>
    );
  }

  /* Desk A — classic: dual top panels + rail + chat */
  if (layout === "desk") {
    return (
      <div className="flex h-full min-h-0 min-w-0 max-w-full flex-1 flex-col overflow-hidden p-4 sm:p-5" data-testid="blu-page" data-blu-layout="desk">
        <div className="mb-4 grid shrink-0 grid-cols-1 gap-3 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)]">
          <section className="neo-panel p-5">
            <div className="mb-3 flex items-end justify-between gap-3">
              <div>
                <div className="label-eyebrow mb-1">BLU</div>
                <div className="font-heading text-xl tracking-tight !font-light">Sessions</div>
              </div>
              <div className="font-heading text-3xl tracking-tight !font-extralight text-[var(--t-accent)]">
                {sortedThreads.length}
              </div>
            </div>
            <p className="text-sm text-[var(--t-meta)]">Scope and invoice threads for the studio.</p>
          </section>
          <section className="neo-panel p-5">
            <div className="label-eyebrow mb-1">Active</div>
            <div className="font-heading text-xl tracking-tight !font-light truncate">{activeThreadTitle}</div>
            <button type="button" onClick={beginNewThread} className="neo-cta mt-4" data-testid="blu-new-thread-header">
              <Plus size={14} strokeWidth={1.4} /> New thread
            </button>
          </section>
        </div>
        <div className="grid min-h-0 flex-1 grid-cols-1 gap-3 lg:grid-cols-[16rem_minmax(0,1fr)]">
          <div className="hidden min-h-0 lg:block">{renderThreadRail()}</div>
          <section className="neo-panel flex min-h-0 flex-col overflow-hidden">
            <div className="shrink-0 border-b border-[var(--t-border)]/40 px-4 py-3 lg:hidden">
              {renderThreadPopover()}
            </div>
            {threadScroll("px-4 pt-4 pb-4")}
            {renderComposer({ mode: "dock" })}
          </section>
        </div>
        {bluStyles}
      </div>
    );
  }

  /* Desk B — merge: Sessions panel owns the thread list; chat is the full right stage */
  if (layout === "desk-merge") {
    return (
      <div className="flex h-full min-h-0 min-w-0 max-w-full flex-1 flex-col overflow-hidden p-4 sm:p-5" data-testid="blu-page" data-blu-layout="desk-merge">
        <div className="grid min-h-0 flex-1 grid-cols-1 gap-3 lg:grid-cols-[minmax(17rem,20rem)_minmax(0,1fr)]">
          <aside className="neo-panel flex min-h-0 flex-col overflow-hidden">
            <div className="shrink-0 border-b border-[var(--t-border)]/40 px-5 py-4">
              <div className="mb-3 flex items-end justify-between gap-3">
                <div>
                  <div className="label-eyebrow mb-1">BLU</div>
                  <div className="font-heading text-xl tracking-tight !font-light">Sessions</div>
                </div>
                <div className="font-heading text-3xl tracking-tight !font-extralight text-[var(--t-accent)]">
                  {sortedThreads.length}
                </div>
              </div>
              <button
                type="button"
                onClick={beginNewThread}
                className="neo-cta w-full justify-center"
                data-testid="blu-new-thread-header"
              >
                <Plus size={14} strokeWidth={1.4} /> New thread
              </button>
            </div>
            <div className="t-scroll-soft min-h-0 flex-1 space-y-1 overflow-y-auto p-2">
              {sortedThreads.map((t) => {
                const on = t.id === bluPack.activeThreadId;
                const last = (t.messages || [])[(t.messages || []).length - 1];
                const preview =
                  last?.role === "user"
                    ? last.content
                    : last?.role === "assistant"
                      ? stripBluFinalizeBlock(last.content || "").slice(0, 72)
                      : "";
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setBluPack((p) => bluPackSetActive(p, t.id))}
                    className={`flex w-full flex-col gap-0.5 rounded-xl px-3 py-2.5 text-left ${
                      on
                        ? "bg-[rgba(0,30,255,0.08)] text-[var(--t-accent)]"
                        : "text-[var(--t-meta)] hover:text-[var(--t-text)]"
                    }`}
                    data-testid={`blu-thread-option-${t.id}`}
                  >
                    <span className="truncate font-heading text-sm !font-light text-[var(--t-text)]">
                      {t.title || "Chat"}
                    </span>
                    {preview ? (
                      <span className="truncate text-[11px] text-[var(--t-meta)]">{preview}</span>
                    ) : null}
                  </button>
                );
              })}
            </div>
          </aside>

          <section className="neo-panel flex min-h-0 flex-col overflow-hidden">
            <div className="flex shrink-0 items-center justify-between gap-3 border-b border-[var(--t-border)]/40 px-5 py-4">
              <div className="min-w-0">
                <div className="label-eyebrow mb-1">Active</div>
                <div className="truncate font-heading text-xl tracking-tight !font-light">
                  {activeThreadTitle}
                </div>
              </div>
              <div className="min-w-0 flex-1 lg:hidden">{renderThreadPopover()}</div>
            </div>
            {threadScroll("px-4 pt-4 pb-4")}
            {renderComposer({ mode: "dock" })}
          </section>
        </div>
        {bluStyles}
      </div>
    );
  }

  /* Desk C — band: slim top status strip, then wide rail + chat */
  if (layout === "desk-band") {
    return (
      <div className="flex h-full min-h-0 min-w-0 max-w-full flex-1 flex-col overflow-hidden p-4 sm:p-5" data-testid="blu-page" data-blu-layout="desk-band">
        <section className="neo-panel mb-3 flex shrink-0 flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex min-w-0 flex-wrap items-baseline gap-x-4 gap-y-1">
            <div>
              <div className="label-eyebrow mb-0.5">BLU</div>
              <div className="font-heading text-xl tracking-tight !font-light">Desk</div>
            </div>
            <div className="hidden h-8 w-px bg-[var(--t-border)] sm:block" aria-hidden />
            <div className="min-w-0">
              <span className="text-[11px] uppercase tracking-[0.14em] text-[var(--t-meta)]">Sessions </span>
              <span className="font-heading text-2xl !font-extralight tabular-nums text-[var(--t-accent)]">
                {sortedThreads.length}
              </span>
            </div>
            <div className="hidden h-8 w-px bg-[var(--t-border)] sm:block" aria-hidden />
            <div className="min-w-0">
              <span className="text-[11px] uppercase tracking-[0.14em] text-[var(--t-meta)]">Active </span>
              <span className="font-heading text-lg !font-light truncate">{activeThreadTitle}</span>
            </div>
          </div>
          <button type="button" onClick={beginNewThread} className="neo-cta self-start sm:self-auto" data-testid="blu-new-thread-header">
            <Plus size={14} strokeWidth={1.4} /> New thread
          </button>
        </section>

        <div className="grid min-h-0 flex-1 grid-cols-1 gap-3 lg:grid-cols-[18rem_minmax(0,1fr)]">
          <div className="hidden min-h-0 lg:block">{renderThreadRail()}</div>
          <section className="neo-panel flex min-h-0 flex-col overflow-hidden">
            <div className="shrink-0 border-b border-[var(--t-border)]/40 px-4 py-3 lg:hidden">
              {renderThreadPopover()}
            </div>
            <div className="hidden shrink-0 items-center justify-between border-b border-[var(--t-border)]/40 px-5 py-3 lg:flex">
              <div className="label-eyebrow mb-0">Thread</div>
              <div className="truncate font-heading text-base !font-light">{activeThreadTitle}</div>
            </div>
            {threadScroll("px-4 pt-4 pb-4")}
            {renderComposer({ mode: "dock" })}
          </section>
        </div>
        {bluStyles}
      </div>
    );
  }

  return (
    <div
      className={`t-fade-up flex h-full min-h-0 max-w-full min-w-0 flex-1 flex-col overflow-hidden ${CHAT_BG}`}
      data-testid="blu-page"
    >
      <header className={`shrink-0 min-w-0 pt-3 sm:pt-6 md:pt-12 ${CHAT_BG}`}>
        <div className="flex min-w-0 items-center gap-3 px-3 py-2.5 sm:px-6 md:px-3">
          <div className="min-w-0 flex-1">{renderThreadPopover()}</div>
        </div>
      </header>
      {threadBodyDefault}
    </div>
  );
}

